import type { BoardImageMode } from './canvasBoard';

/**
 * The user asked to change how images enter the video.
 * "Criar keyframes" and "gerar mais referências" are not a mode change.
 */
const REFERENCE_REQUEST =
  /\b(?:(?:s[oó]|apenas|somente)\s+(?:como\s+)?refer[eê]ncias?|refer[eê]ncias?\s+e\s+n[aã]o\s+(?:como\s+)?(?:keyframes?|quadros?[- ]chave)|n[aã]o\s+(?:s[aã]o\s+|como\s+|use\s+como\s+)?(?:keyframes?|quadros?[- ]chave)|references?\s+(?:only|not\s+keyframes?)|(?:como|use\s+como|s[oó]\s+como|modo|use\s+as)\s+refer[eê]ncias?|entrada\s+de\s+refer[eê]ncias?)\b/i;

const CONTINUOUS_REQUEST =
  /\b(?:modo\s+cont[ií]nuo|entrada\s+cont[ií]nua|passe?\s+exatamente\s+por|(?:como|use\s+como|s[aã]o)\s+(?:keyframes?|quadros?[- ]chave)|uma imagem liga na outra)\b/i;

/**
 * The card is Referências and the user did not ask for Contínuo.
 * That card does not generate extra frames.
 */
export function keepsReferenceImages(
  cardMode: BoardImageMode | undefined,
  explicitMode: BoardImageMode | undefined,
): boolean {
  return cardMode === 'reference' && explicitMode !== 'keyframes';
}

/** Newest explicit input-mode request. Form summaries are not requests. */
export function requestedImageMode(texts: string[]): BoardImageMode | undefined {
  for (let i = texts.length - 1; i >= 0; i--) {
    const t = String(texts[i] || '').trim();
    if (!t || /^Respostas do formulário:/i.test(t)) continue;
    if (REFERENCE_REQUEST.test(t)) return 'reference';
    if (CONTINUOUS_REQUEST.test(t)) return 'keyframes';
  }
  return undefined;
}

const YES_KEYFRAMES =
  /\b(?:sim,?\s+criar\s+keyframes?|sim,?\s+gerar\s+mais\s+refer[eê]ncias?|gerar\s+mais\s+refer[eê]ncias?|(?:crie|criar|gere|gerar|fa[cç]a|fazer)\s+(?:as\s+|novas?\s+|intermedi[aá]rias?\s+)*(?:keyframes?|quadros?[- ]chave)|keyframes?\s+intermedi[aá]rias?)\b/i;

const NO_KEYFRAMES =
  /\b(?:n[aã]o\b.{0,40}\b(?:keyframes?|quadros?[- ]chave)|n[aã]o,?\s+s[oó]\s+as\s+imagens|s[oó]\s+as\s+imagens\s+(?:da\s+tela|que\s+j[aá])|sem\s+(?:criar|gerar)\s+(?:as\s+)?(?:keyframes?|imagens|quadros))\b/i;

/**
 * The user answered the keyframe question, newest text first.
 * "Sim, criar keyframes" is a yes even while the card is still on Referências.
 */
export function keyframeChoice(texts: string[]): 'yes' | 'no' | undefined {
  for (let i = texts.length - 1; i >= 0; i--) {
    const t = String(texts[i] || '').trim();
    if (!t) continue;
    if (/^Respostas do formulário:/i.test(t)) {
      if (/"keyframes"\s*:\s*"sim"/i.test(t)) return 'yes';
      if (/"keyframes"\s*:\s*"(?:nao|não|false|no)"/i.test(t)) return 'no';
      continue;
    }
    if (NO_KEYFRAMES.test(t)) return 'no';
    if (YES_KEYFRAMES.test(t)) return 'yes';
  }
  return undefined;
}

/** "5 cenas" / form `"scene_count":"2"` from the newest text that names a count. */
export function requestedSceneCount(texts: string[]): number | undefined {
  for (let i = texts.length - 1; i >= 0; i--) {
    const t = String(texts[i] || '');
    const form = t.match(/"scene_count"\s*:\s*"?(\d{1,2})"?/i);
    if (form) {
      const n = Number(form[1]);
      if (n >= 1 && n <= 10) return n;
    }
    const match = t.match(/\b(\d{1,2})\s*(?:cenas?|refer[eê]ncias?)\b/i);
    if (!match) continue;
    const n = Number(match[1]);
    if (n >= 1 && n <= 10) return n;
  }
  return undefined;
}

/**
 * Total scenes after the user confirmed intermediate keyframes.
 * Honors an explicit count above the photo count. If they said yes to keyframes but left
 * the total at the photo count (form minimum), add a small gap for intermediates — never a
 * hard floor of 5 (that turned “2 cenas” + 1 foto into 4 generated frames).
 */
export function keyframeSceneCount(intentCount: number, imageCount: number, texts: string[]): number {
  const photos = Math.max(0, imageCount);
  const asked = requestedSceneCount(texts);
  const explicit = asked ?? (intentCount > 0 ? intentCount : undefined);
  if (explicit != null && explicit > photos) return Math.min(10, explicit);
  // Keyframes on, but total ≤ photos: need at least one new beat (prefer +2 when there is room).
  const extras = photos <= 1 ? 1 : Math.min(2, Math.max(1, 10 - photos));
  return Math.min(10, Math.max(photos + extras, photos + 1));
}

const FRAME_RECOMMENDATION =
  /keyframe|quadro[- ]chave|mais refer[eê]ncias|gerar (?:mais )?refer[eê]ncias|recomendo que seja cont[ií]nuo|cenas intermedi|cenas entre/i;

/** Shown when a reply only recommended extra frames. The script still has to be built from the photos. */
export const REFERENCE_PHOTOS_NOTE = 'As fotos entram como referência, sem quadros novos.';

/**
 * Referências skipped the keyframe question and the agent did not mount a script.
 * The client builds that script from the photos already on the card.
 */
export function shouldBuildReferenceScript(args: {
  onReference: boolean;
  droppedKeyframeAsk: boolean;
  reply: string;
  hasStoryboard: boolean;
}): boolean {
  if (!args.onReference || args.hasStoryboard) return false;
  if (args.droppedKeyframeAsk) return true;
  return args.reply.trim() === REFERENCE_PHOTOS_NOTE;
}

/** Chips that would invite extra stills while the card is Referências. */
export function withoutExtraFrameSuggestions(list: string[] | undefined): string[] | undefined {
  if (!list) return list;
  return list.filter((s) => !FRAME_RECOMMENDATION.test(s));
}

/** Drops sentences that recommend extra frames. A reply that was only that recommendation gets a plain note. */
export function withoutFrameRecommendation(text: string): string {
  const parts = text.split(/(?<=[.!?])\s+/).map((p) => p.trim()).filter(Boolean);
  const kept = parts.filter((p) => !FRAME_RECOMMENDATION.test(p));
  const next = kept.join(' ').trim();
  return next || REFERENCE_PHOTOS_NOTE;
}
